import axios from 'axios';
import useAuthStore from '../store/useAuthStore';

const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'https://legalcheck.com.co/api/v1', // Asegúrate que la URL base sea la correcta
    // Por defecto dejamos que axios asigne Content-Type; lo eliminamos en el interceptor si la data es FormData.
    headers: {
        'Accept': 'application/json',
    },
});

// Si se envía FormData en una petición, axios podrá establecer el Content-Type correcto
// (incluyendo el boundary). En el interceptor nos encargamos de eliminar cualquier
// Content-Type previo que pudiera haberse dejado por error.

api.interceptors.request.use(config => {
    const token = useAuthStore.getState().token;
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    // Si la petición lleva un FormData, quitamos Content-Type para que axios lo genere
    if (config.data && config.data instanceof FormData) {
        delete config.headers['Content-Type'];
        // opcional: podríamos forzar "multipart/form-data" aquí, pero axios lo hace automáticamente
        // config.headers['Content-Type'] = 'multipart/form-data';
    }

    return config;
}, error => Promise.reject(error));

// Request Interceptor: Añade el token a cada petición
api.interceptors.request.use(config => {
    const token = useAuthStore.getState().token;
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
}, error => Promise.reject(error));

// Response Interceptor: en un 401 intenta renovar el access token con el refresh
// token y reintenta la petición original. Solo cierra sesión si el refresh falla.
let isRefreshing = false;
let pendingRequests = [];

const flushQueue = (newToken) => {
    pendingRequests.forEach((cb) => cb(newToken));
    pendingRequests = [];
};

api.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;
        const status = error.response?.status;
        const url = originalRequest?.url || '';
        // No intentar refresh para los propios endpoints de auth (login/refresh)
        const isAuthCall = url.includes('/auth/refresh') || url.includes('/auth/login');

        // No es 401, es una llamada de auth, o ya se reintentó: propagar el error.
        if (status !== 401 || isAuthCall || originalRequest?._retry) {
            return Promise.reject(error);
        }

        const refresh_token = sessionStorage.getItem('refresh_token');
        if (!refresh_token) {
            useAuthStore.getState().logout();
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        // Si ya hay un refresh en curso, encolar esta petición hasta que termine.
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                pendingRequests.push((newToken) => {
                    if (!newToken) return reject(error);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    resolve(api(originalRequest));
                });
            });
        }

        isRefreshing = true;
        try {
            const newToken = await useAuthStore.getState().refreshToken();
            flushQueue(newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
        } catch (refreshError) {
            flushQueue(null);
            useAuthStore.getState().logout();
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export default api;